/**
 * DXF block export for door/window opening glyphs.
 *
 * Each unique opening shape (kind, door type, size, wall thickness, swing
 * flags) becomes one BLOCK definition in opening-local coordinates; every
 * placed opening becomes a single INSERT of it. The glyph is rebuilt with an
 * identity frame — `buildOpeningGlyph` is a pure function of its frame, and
 * opening frames are always right-handed (swing/hinge mirroring is baked into
 * the geometry by the flags), so a rotation-only INSERT reproduces every
 * placement exactly. Door swing arcs export as true bulged polylines with
 * their chord tessellation skipped. Openings on CURVED walls bend their
 * glyph along the wall (see openingWallMap.ts), so no rigid block fits them:
 * they export as loose lines.
 */

import type { DxfBlock, DxfColor, DxfEntity, DxfInsertEntity } from '@pointorama/dxf-exporter';
import { OpeningKind } from 'shared-types';
import { buildOpeningGlyph, customGlyphTypeOf, type OpeningGlyphInput } from '../../potree/utils/skeleton/openingGlyphs';
import { isBentOpeningFrame, localOpeningFrame } from '../../potree/utils/skeleton/openingWallMap';
import { dxfBulgeFromInternal } from '../../potree/utils/skeleton/arcGeometry';

type PointConverter = (point: { x: number; y: number; z: number }) => { x: number; y: number; z: number };

/** One registry per export run — deduplicates blocks across floors/sketches. */
export class OpeningDxfBlockRegistry {
  private readonly blocks: DxfBlock[] = [];
  private readonly nameByKey = new Map<string, string>();
  private readonly prefixCounters = new Map<string, number>();

  getBlocks(): DxfBlock[] {
    return this.blocks;
  }

  /**
   * The block name for this opening's shape, building the block on first
   * sight of the dedup key. Null when the shape has no geometry (open
   * doorway, degenerate width).
   */
  ensureBlock(input: OpeningGlyphInput, cp: PointConverter): string | null {
    const mm = (v: number) => Math.round(v * 1000);
    const flags = `s${input.swingPositive ? 1 : 0}|r${input.rollable ? 1 : 0}`;
    const customType = customGlyphTypeOf(input);
    // Windows ignore wall thickness, door type and hinge (fixed glass/roller
    // symbol) — a wider key would split identical window blocks per wall.
    // Organisation symbols may read everything (wall, height, params).
    const key = customType
      ? `${input.kind}|${customType}|w${mm(input.frame.halfWidth * 2)}|t${mm(input.wallThickness)}|H${mm(input.height ?? 0)}|h${input.hingeAtStart ? 1 : 0}|${flags}|${JSON.stringify(input.customParams ?? {})}`
      : input.kind === OpeningKind.WINDOW
        ? `${input.kind}|w${mm(input.frame.halfWidth * 2)}|${flags}`
        : `${input.kind}|${input.doorType}|w${mm(input.frame.halfWidth * 2)}|t${mm(input.wallThickness)}|h${input.hingeAtStart ? 1 : 0}|${flags}`;

    const existing = this.nameByKey.get(key);
    if (existing) return existing;

    // Identity frame: block geometry equals the world glyph up to rotation +
    // translation (frames are never mirrored).
    const glyph = buildOpeningGlyph({ ...input, frame: localOpeningFrame(input.frame.halfWidth) }, 0);
    if (glyph.segments.length === 0) return null;

    const typeName = (type: string) => type.toUpperCase().replace(/[^A-Z0-9_-]/g, '_');
    const prefix = input.kind === OpeningKind.WINDOW ? `POINTORAMA_WINDOW${customType ? `_${typeName(customType)}` : ''}` : `POINTORAMA_DOOR_${typeName(customType ?? input.doorType)}`;
    const count = (this.prefixCounters.get(prefix) ?? 0) + 1;
    this.prefixCounters.set(prefix, count);
    const blockName = `${prefix}_${count}`;
    this.nameByKey.set(key, blockName);

    const entities: DxfEntity[] = [];
    for (let i = 0; i + 5 < glyph.segments.length; i += 6) {
      if (glyph.arcs.some((a) => i >= a.segStart && i < a.segEnd)) continue;
      entities.push({
        type: 'LINE',
        start: cp({ x: glyph.segments[i], y: glyph.segments[i + 1], z: 0 }),
        end: cp({ x: glyph.segments[i + 3], y: glyph.segments[i + 4], z: 0 }),
        colorAci: 0,
        layerName: '0',
      });
    }
    for (const arc of glyph.arcs) {
      entities.push({
        type: 'POLYLINE',
        points: [cp({ x: arc.start.x, y: arc.start.y, z: 0 }), cp({ x: arc.end.x, y: arc.end.y, z: 0 })],
        closed: false,
        bulges: [dxfBulgeFromInternal(arc.bulge)],
        colorAci: 0,
        layerName: '0',
      });
    }

    this.blocks.push({ name: blockName, entities });
    return blockName;
  }
}

/**
 * An opening as a single INSERT of its (shared) block definition, or null
 * when the opening draws nothing (open doorway).
 */
export function openingToDxfInsert(input: OpeningGlyphInput, layerName: string, color: DxfColor | undefined, cp: PointConverter, registry: OpeningDxfBlockRegistry): DxfInsertEntity | null {
  if (isBentOpeningFrame(input.frame)) return null;
  const blockName = registry.ensureBlock(input, cp);
  if (!blockName) return null;
  return {
    type: 'INSERT',
    blockName,
    position: cp({ x: input.frame.center.x, y: input.frame.center.y, z: 0 }),
    rotation: (Math.atan2(input.frame.wallDir.y, input.frame.wallDir.x) * 180) / Math.PI,
    color,
    layerName,
  };
}

/**
 * An opening's DXF entities: one INSERT of its shared block, or — on a
 * curved wall, where the glyph bends — its world-space glyph as loose lines.
 */
export function openingToDxfEntities(input: OpeningGlyphInput, layerName: string, color: DxfColor | undefined, cp: PointConverter, registry: OpeningDxfBlockRegistry): DxfEntity[] {
  if (!isBentOpeningFrame(input.frame)) {
    const insert = openingToDxfInsert(input, layerName, color, cp, registry);
    return insert ? [insert] : [];
  }
  const { segments } = buildOpeningGlyph(input, 0);
  const entities: DxfEntity[] = [];
  for (let i = 0; i + 5 < segments.length; i += 6) {
    entities.push({
      type: 'LINE',
      start: cp({ x: segments[i], y: segments[i + 1], z: 0 }),
      end: cp({ x: segments[i + 3], y: segments[i + 4], z: 0 }),
      color,
      layerName,
    });
  }
  return entities;
}
